import { RouteComponentProps } from 'react-router-dom';
import { AxiosError } from 'axios';
import HTMLEditor from 'components/form/HTMLEditor';
import { customToolbarEpd } from 'components/form/customToolbar';
import TitleAndBack from 'components/v2/TitleAndBack';
import { PrimaryButton, SecondaryButton, ToolButton } from 'components/v2/buttons';
import CollectionEpdList from 'components/v2/collections/CollectionEpdList';
import CollectionGuide from 'components/v2/collections/CollectionGuide';
import CollectionImageUpload from 'components/v2/collections/CollectionImageUpload';
import EpdItem from 'components/v2/collections/EpdItem';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { confirmQuestion } from 'components/v2/confirm-dialog/confirmQuestion';
import { HelpBox } from 'components/v2/help-boxes';
import { InfoErrorContainer } from 'components/v2/help-boxes/InfoErrorContainer';
import InfoIcon from 'components/v2/icons/TooltipInfoIcon';
import { toaster } from 'components/v2/toast';
import { ProcessStatus } from 'constants/constants';
import { Role } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Field, Form, Formik, FormikValues } from 'formik';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ListBox } from 'primereact/listbox';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import CollectionServiceV2 from 'services/CollectionServiceV2';
import CompanyService from 'services/CompanyService';
import {
  AccountModel,
  ActionState,
  CollectionEPDModel,
  CollectionModel,
  EPDCollectionStatus,
  EPDStatusDisplayName,
  FileParameter,
  SaveCollectionModel,
} from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import styled, { css } from 'styled-components';
import { Container, Label } from 'styles/Styles.styled';
import Spinner from 'styles/spinner.styled';
import { MainViewFull, WizardHeaderGrid } from 'styles/v2/Styles.styled';
import * as Yup from 'yup';
import EpdRoutes from 'routes/EpdRoutes';
import { withGreyBackground } from '../WithGreyBackground';
import CollectionService from 'services/CollectionService';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Title is required'),
  description: Yup.string().test('is-not-empty', ' ', (value: any) => value && value.replace(/<[^>]*>/g, '').trim() !== ''),
});

const initialValues: CollectionModel = {
  id: null,
  name: '',
  description: '',
  image: null,
  status: EPDCollectionStatus.Created,
  epds: [],
};

const EditCollection: React.FC<RouteComponentProps<{ collectionId: string }>> = ({ history, match }) => {
  const [collection, setCollection] = React.useState<CollectionModel | undefined>(undefined);
  const [account, setAccount] = useState<AccountModel>({});
  const { changeCompany } = React.useContext(CompanyContext);
  const [allEpds, setAllEpds] = useState<CollectionEPDModel[]>([]);
  const [currentEpds, setCurrentEpds] = useState<CollectionEPDModel[]>([]);
  const [currentImage, setCurrentImage] = useState<any>();
  const [currentImageFile, setCurrentImageFile] = useState<any>();
  const [popupAddEpdList, setPopupAddEpdList] = useState<CollectionEPDModel[]>([]);
  const [filterText, setFilterText] = useState('');
  const { t } = useTranslation();
  const { companyId } = React.useContext(CompanyContext);
  const visibleEditorStyle = {
    border: '1px solid #F1F1F1',
    padding: '0 1rem',
    fontSize: '14px',
    height: 'fit-content',
    minHeight: '100px',
  };
  const [openedEpdChooser, setOpenedEpdChooser] = useState<boolean>(false);
  const formRef = useRef<FormikValues | any>();
  const [processStatus, setProcessStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const handleAddEpd = async () => {
    setCurrentEpds([...currentEpds, ...popupAddEpdList]);

    setPopupAddEpdList([]);
    setOpenedEpdChooser(false);
  };

  const handleCancelAddEpd = async () => {
    setOpenedEpdChooser(false);
    setPopupAddEpdList([]);
  };

  let { collectionId } = match.params;

  const onRemoveFile = async () => {
    setCurrentImage(null);
    setCurrentImageFile(null);
  };

  const onChangeCollection = async (propertyName: string, val: FileParameter) => {
    if (!collectionId) {
      return;
    }
    await CollectionServiceV2.addCollectionFile(collectionId, propertyName, val);
  };

  const onRemoveEpd = async (epdId: string) => {
    setCurrentEpds(currentEpds.filter((item) => item.id !== epdId));
  };

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 1000000) {
        toaster({ severity: 'error', summary: 'Image too large', details: 'Image size is more than 1MB.' });
      } else {
        setCurrentImageFile(file);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          setCurrentImage(reader.result);
        };
      }
    }
  };

  const uploadImage = async (propertyName: string) => {
    if (!currentImageFile || !collectionId) {
      return;
    }

    const reader = new FileReader();
    let resolveUploading: (value?: undefined) => void;
    let rejectUploading: (reason?: any) => void;
    const uploadingPromise = new Promise((resolve, reject) => {
      resolveUploading = resolve;
      rejectUploading = reject;
    });
    reader.onload = async () => {
      const fileBlob = reader.result as ArrayBuffer;
      if (!fileBlob) {
        rejectUploading(new Error('No file blob after reading file.'));
        return;
      }
      const blob = new Blob([new Uint8Array(fileBlob)], { type: currentImageFile.type });
      try {
        await onChangeCollection(propertyName, {
          fileName: currentImageFile.name,
          data: blob,
        });
        resolveUploading();
      } catch (e) {
        rejectUploading(new Error('Error uploading an image. Try another file.'));
      }
    };
    await reader.readAsArrayBuffer(currentImageFile);
    await uploadingPromise;
  };

  const fetchCollection = async () => {
    try {
      const result = await CollectionServiceV2.getCollection(collectionId);
      setCollection(result);
      setCurrentEpds(result.epds);
      setCurrentImage(result.image);
    } catch (error) {
      if (error instanceof AxiosError) {
        switch (error.status) {
          case 403:
            history.push(EpdRoutes.forbidden);
            break;
          case 404:
            history.push(EpdRoutes.notFound);
            break;
        }
      }
      console.error(error);
    }
  };

  const fetchEpds = async () => {
    if (!account?.id || !companyId) {
      setAllEpds([]);
      return;
    }

    try {
      const result = await CollectionService.getAllEPDs(companyId, account.id);
      setAllEpds(result);
    } catch {}
  };

  const fetchAccount = async () => {
    if (companyId) {
      try {
        const res = await CompanyService.getCompany(companyId);
        changeCompany?.(companyId);
        res?.accounts && setAccount(res?.accounts[0]);
      } catch {}
    } else {
    }
  };

  useEffect(() => {
    fetchAccount();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!account?.id) return;

    if (collectionId != 'new') {
      fetchCollection();
      fetchEpds();
      setIsEditMode(false);
    } else {
      setCollection(initialValues);
      fetchEpds();
      setIsEditMode(true);
    }
  }, [collectionId, account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!collection) {
    return <div>${t('messages.loading')}</div>;
  }

  const onSave = async (values: any) => {
    try {
      if (!currentImage) {
        toaster({ severity: 'error', summary: 'No Image', details: 'Collection image is mandatory' });
        return;
      }
      setProcessStatus(ProcessStatus.Fetching);

      const data: SaveCollectionModel = {
        ...values,
        companyId,
        epdIds: currentEpds.map((i) => i.id),
        image: currentImageFile,
      };
      const newCollectionId = await CollectionServiceV2.saveCollection(data);
      if (newCollectionId) {
        collectionId = newCollectionId;
      }
      await uploadImage('ProductImage');
      await fetchCollection();
      toaster({ severity: 'success', summary: 'Success', details: 'Collection has been saved' });
      setIsEditMode(false);
      setProcessStatus(ProcessStatus.Success);
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: (error as Error)?.message ?? JSON.stringify(error),
      });
      setProcessStatus(ProcessStatus.Error);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const isOwner =
        (
          await MembershipService.getMembershipsByUserAndCompanyAndRole(
            companyId || '',
            Role.EPDOwner,
            AuthService.getUser()?.id!
          )
        ).length > 0;

      if (isOwner || AuthService.isAdmin()) {
        setProcessStatus(ProcessStatus.Fetching);

        await CollectionServiceV2.publishCollection(id);
        collectionId = id;

        await fetchCollection();
        toaster({
          severity: 'success',
          summary: 'The collection has been published!',
          details: 'Now it is available on EPD website in EPD library.',
        });
        setProcessStatus(ProcessStatus.Success);
      } else {
        toaster({
          severity: 'error',
          summary: 'Collection not published!',
          details:
            'Unfortunately, you are not authorized to publish this collection. Please contact the EPD Owner who is authorized to publish this.',
        });
      }
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: JSON.stringify(error) });
      setProcessStatus(ProcessStatus.Error);
    }
  };

  const handleDepublish = async (id: string) => {
    try {
      const isOwner =
        (
          await MembershipService.getMembershipsByUserAndCompanyAndRole(
            companyId || '',
            Role.EPDOwner,
            AuthService.getUser()?.id!
          )
        ).length > 0;
      if (isOwner || AuthService.isAdmin()) {
        setProcessStatus(ProcessStatus.Fetching);
        await CollectionServiceV2.depublishCollection(id);
        await fetchCollection();
        toaster({
          severity: 'success',
          summary: 'Success',
          details: 'The collection has been depublished.',
        });
        setProcessStatus(ProcessStatus.Success);
      } else {
        toaster({
          severity: 'error',
          summary: 'Collection not depublished!',
          details:
            'Unfortunately, you are not authorized to depublish this collection. Please contact the EPD Owner who is authorized to depublish this.',
        });
      }
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: JSON.stringify(error) });
      setProcessStatus(ProcessStatus.Error);
    }
  };

  const acceptCollectionDelete = async () => {
    try {
      await CollectionServiceV2.deleteCollection(collection.id!);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: 'The collection has been deleted.',
      });
      history.goBack();
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: JSON.stringify(error) });
      console.error(error);
    }
  };

  const isFormFieldInvalid = (name: string) =>
    formRef.current && !!(formRef.current.touched[name] && formRef.current.errors[name]);

  const epdChooserFooterContent = (
    <div>
      <SecondaryButton label="Cancel" onClick={handleCancelAddEpd} />
      <PrimaryButton label="Add" onClick={handleAddEpd} autoFocus />
    </div>
  );

  const avoidFormSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const getFilteredEpds = (allEpds: CollectionEPDModel[], currentEpds: CollectionEPDModel[], filterText: string) => {
    return allEpds.filter(
      (item) =>
        !currentEpds.find((i) => i.id === item.id) &&
        item.statusDisplayName !== EPDStatusDisplayName.Deregistered &&
        item.name?.toLocaleLowerCase().includes(filterText.toLocaleLowerCase())
    );
  };

  const toggleSelectAll = (
    allEpds: CollectionEPDModel[],
    currentEpds: CollectionEPDModel[],
    filterText: string,
    popupAddEpdList: CollectionEPDModel[],
    setPopupAddEpdList: React.Dispatch<React.SetStateAction<CollectionEPDModel[]>>
  ) => {
    const filteredEpds = getFilteredEpds(allEpds, currentEpds, filterText);
    if (popupAddEpdList.length === filteredEpds.length) {
      setPopupAddEpdList([]);
    } else {
      setPopupAddEpdList(filteredEpds);
    }
  };

  return (
    <Container>
      <ConfirmDialog />
      {processStatus == ProcessStatus.Fetching && <Spinner />}
      <MainViewFull>
        <WizardHeaderGrid>
          <TitleAndBack
            title={collection.id == null ? 'Create collection' : collection.name!}
            backTo="Back to collections"
            onBack={() => history.goBack()}
          />
          <div className="flex align-items-center gap-3">
            {isEditMode && (
              <>
                <SecondaryButton
                  label="Cancel"
                  outlined
                  visible={collection.collectionState?.deleteCollection != ActionState.Hidden}
                  disabled={processStatus == ProcessStatus.Fetching}
                  onClick={() => {
                    confirmQuestion({
                      caption: collection.id ? 'Do you want to cancel edit' : 'Do you want to cancel collection creation?',
                      description: collection.id ? (
                        <div>
                          The collection edits will not be saved.<p>All edits will be lost.</p>
                        </div>
                      ) : (
                        <div>
                          The collection creation will be cancelled.<p>All the data will be lost.</p>
                        </div>
                      ),
                      acceptLabel: 'Cancel',
                      rejectLabel: 'Discard',
                      onConfirm: history.goBack,
                    });
                  }}
                />
                <PrimaryButton
                  label="Save collection"
                  disabled={processStatus == ProcessStatus.Fetching}
                  visible={collection.collectionState?.saveCollection !== ActionState.Hidden}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.handleSubmit();
                    }
                  }}
                />
              </>
            )}

            {!isEditMode && collection.status === EPDCollectionStatus.Published && (
              <>
                <SecondaryButton
                  label="Depublish"
                  outlined
                  visible={collection.collectionState?.depublishCollection !== ActionState.Hidden}
                  onClick={() => {
                    handleDepublish(formRef.current!.values.id);
                  }}
                />
              </>
            )}

            {!isEditMode && collection.status !== EPDCollectionStatus.Published && (
              <>
                <PrimaryButton
                  label="Publish"
                  visible={collection.collectionState?.publishCollection !== ActionState.Hidden}
                  onClick={() => {
                    handlePublish(formRef.current!.values.id);
                  }}
                  disabled={!collection.hasPublishedEPDs}
                />
              </>
            )}
            {!isEditMode && (
              <>
                <ToolButton
                  icon="pi pi-file-edit text-2xl"
                  aria-label="Edit"
                  visible={collection?.collectionState?.editCollection !== ActionState.Hidden}
                  onClick={() => {
                    setIsEditMode(true);
                  }}
                />
                <ToolButton
                  icon="pi pi-trash text-2xl"
                  aria-label="Delete"
                  visible={collection?.collectionState?.deleteCollection !== ActionState.Hidden}
                  onClick={() => {
                    confirmDelete({
                      caption: 'Are you sure you want to delete the collection?',
                      description: 'The collection will be deleted and we will not be able to restore it.',
                      onConfirm: () => {
                        acceptCollectionDelete();
                      },
                    });
                  }}
                />
              </>
            )}
          </div>
        </WizardHeaderGrid>

        {isEditMode && <CollectionGuide />}

        {!collection.hasPublishedEPDs && !isEditMode && (
          <InfoErrorContainer
            infoText={'This collection cannot be published yet, as none of the selected EPDs are published.'}
          />
        )}

        <CollectionForm>
          <Formik
            innerRef={formRef!}
            enableReinitialize
            initialValues={collection}
            onSubmit={onSave}
            validationSchema={validationSchema}
          >
            {({ values, handleChange }) => (
              <Form onKeyDown={avoidFormSubmitOnEnter}>
                <Column>
                  <FixedRow>
                    <FixedLabel>
                      <Label required>EPD COLLECTION NAME</Label>
                      <InfoIcon content={t('manageCollections.tooltips.title')} />
                    </FixedLabel>
                    <Field
                      as={InputText}
                      maxLength={300}
                      id="name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      className={isFormFieldInvalid('name') && 'p-invalid'}
                      disabled={!isEditMode}
                    />
                  </FixedRow>
                </Column>

                <Column>
                  <FixedRow>
                    <FixedLabel>
                      <Label required> EPD Collection description</Label>
                      <InfoIcon content={t('manageCollections.tooltips.profile')} />
                    </FixedLabel>
                    <HTMLEditor
                      name="description"
                      editorStyle={visibleEditorStyle}
                      toolbarHidden={!isEditMode}
                      toolbar={customToolbarEpd}
                      toolbarClassName={'p-enabled-rdw'}
                      readOnly={!isEditMode}
                      stripPastedStyles={true}
                      editorClassName={isFormFieldInvalid('description') && 'p-invalid'}
                    />
                  </FixedRow>
                </Column>

                <Column>
                  <FixedRow>
                    <FixedLabel>
                      <Label required>Collection cover image</Label>
                      <InfoIcon content={t('manageCollections.tooltips.image')} />
                    </FixedLabel>

                    {isEditMode && (
                      <HelpBox>
                        <span>
                          The added image serves as a cover to present your collection in the EPD Library at{' '}
                          <a target="_blank" rel="noopener noreferrer" href="http://www.environdec.com">
                            www.environdec.com.
                          </a>
                        </span>
                        Accepted formats: PNG, JPG, JPEG. Recommended file size: less than 1 MB per file.
                      </HelpBox>
                    )}

                    <UploadBox>
                      <CollectionImageUpload
                        name={t('manageCollections.image')}
                        image={currentImage}
                        onRemoveImage={onRemoveFile}
                        disabled={!isEditMode}
                        onChange={handleImageChange}
                      />
                    </UploadBox>
                  </FixedRow>
                </Column>
              </Form>
            )}
          </Formik>

          <Column>
            <FixedRow>
              <FixedLabel>
                <Label>Add EPD to the collection</Label>
                <InfoIcon content={'Manage the list of EPDs to be displayed in this collection.'} />
              </FixedLabel>

              {isEditMode && <HelpBox>Please select the EPD from your library.</HelpBox>}

              {isEditMode && (
                <div>
                  <Button label="+ Add EPD" text onClick={() => setOpenedEpdChooser(true)} />
                </div>
              )}
              <Dialog
                header="Add EPD"
                visible={openedEpdChooser}
                style={{ width: '50vw' }}
                onHide={() => setOpenedEpdChooser(false)}
                footer={epdChooserFooterContent}
              >
                <div>
                  <SelectAllPanel>
                    <Button
                      label={
                        popupAddEpdList.length === getFilteredEpds(allEpds, currentEpds, filterText).length
                          ? 'Deselect all'
                          : 'Select all'
                      }
                      text
                      onClick={() => toggleSelectAll(allEpds, currentEpds, filterText, popupAddEpdList, setPopupAddEpdList)}
                    />
                  </SelectAllPanel>
                  <ListBox
                    filter
                    filterPlaceholder="Search EPD"
                    multiple
                    filterValue={filterText}
                    onFilterValueChange={(e) => setFilterText(e.value)}
                    value={popupAddEpdList}
                    onChange={(e) => setPopupAddEpdList(e.value)}
                    options={getFilteredEpds(allEpds, currentEpds, filterText)}
                    optionLabel="name"
                    className="w-full"
                    itemTemplate={(epd) => <EpdItem epd={epd} selected={popupAddEpdList.includes(epd)} />}
                    listStyle={{ maxHeight: '50vh' }}
                  />
                </div>
              </Dialog>
              <CollectionEpdList epds={currentEpds} isEditMode={isEditMode} onRemoveEpd={onRemoveEpd} />
            </FixedRow>
          </Column>
        </CollectionForm>
      </MainViewFull>
    </Container>
  );
};

const SelectAllPanel = styled.div`
  margin-top: 0.75rem;
  float: right;
  margin-right: 1rem;
`;

const CollectionForm = styled.div`
  width: 100%;
  background-color: #ffffff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 1rem 2rem;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  box-sizing: border-box;
  margin: 1rem 0;
`;

export const FixedRow = styled.div`
  grid-column-gap: 10px;
  display: grid;
  grid-template-columns: 100%;
`;

export const FixedLabel = styled.label<{ required?: boolean }>`
  font-weight: normal;
  text-transform: uppercase;
  ${(props) => props.theme.fonts.textSmall}
  flex: 0 0 200px;
  align-self: auto;
  margin: 0.5rem 1rem 0.5rem 0; /*risky change? */

  > svg {
    margin: 0 0.5rem;
  }

  > div + svg {
    margin: 0;
  }
  ${(props) =>
    props.required &&
    css`
      &:after {
        content: ' *';
      }
    `}
`;

const UploadBox = styled.div`
  display: flex;
`;

export default withGreyBackground(EditCollection);
